import React from 'react';
import { arrayOf, string } from 'prop-types';
import { formatPhone } from '../../utils/formats';
import { isExternalLink } from '../../utils/url';

function StaffCard({
  id: id,
  Image: image,
  FullName: fullName,
  Position: positions,
  Department: department,
  PhoneNumber: phoneNumber,
  EmailAddress: emailAddress,
  ProfileUrl: profileUrl,
  emailAriaLabel,
  phoneAriaLabel,
  externalLinkText
}) {
  const isExternal = isExternalLink(profileUrl);

  return (
    <article
      className={`staff-card ${
        isExternal
          ? 'faculty-staff-listing__external-card faculty-staff-listing__external-card--no-image'
          : ''
      }`}
      aria-labelledby={id}
    >
      <div className="staff-card__desktop-wrapper">
        {image && (
          <div
            className="staff-card__image"
            style={{ backgroundImage: `url(${image})` }}
            aria-label={fullName}
            role="img"
          />
        )}
      </div>
      <div className="staff-card__wrapper">
        <div className="staff-card__info">
          <div className="staff-card__mobile-wrapper">
            {image && (
              <img
                className="staff-card__image"
                data-object-fit="cover"
                src={image}
                alt={fullName}
                loading="lazy"
                height="80"
                width="80"
              />
            )}
            <div className="staff-card__personal-info">
              <h2 className="staff-card__fullname">
                <a
                  className={
                    isExternal ? 'faculty-staff-listing__external-link' : null
                  }
                  href={profileUrl}
                  target={isExternal ? '_blank' : null}
                  rel={isExternal ? 'noopener noreferrer' : null}
                >
                  {isExternal && (
                    <span className="visually-hidden">{externalLinkText}</span>
                  )}
                  <span id={id}>{fullName}</span>
                </a>
              </h2>
              {positions?.length > 0 &&
                positions.map((position, index) => (
                  <h3
                    className={`staff-card__position ${
                      index ? 'staff-card__position--hidden' : ''
                    }`}
                    key={position}
                  >
                    {position}
                  </h3>
                ))}
              {department && (
                <h4 className="staff-card__department">{department}</h4>
              )}
            </div>
          </div>
        </div>
        {(phoneNumber || emailAddress) && (
          <ul className="staff-card__additional-info">
            {phoneNumber && (
              <li className="staff-card__additional-info-item">
                <a
                  aria-label={`${phoneAriaLabel} ${formatPhone(phoneNumber)}`}
                  href={`tel:${phoneNumber}`}
                >
                  <svg>
                    <use xlinkHref="#phone-outline" />
                  </svg>
                  {phoneNumber}
                </a>
              </li>
            )}
            {emailAddress && (
              <li className="staff-card__additional-info-item">
                <a
                  aria-label={`${emailAriaLabel} ${emailAddress}`}
                  href={`mailto:${emailAddress}`}
                >
                  <svg>
                    <use xlinkHref="#envelope-outline" />
                  </svg>
                  {emailAddress}
                </a>
              </li>
            )}
          </ul>
        )}
      </div>
    </article>
  );
}

StaffCard.propTypes = {
  emailAriaLabel: string,
  phoneAriaLabel: string,
  id: string.isRequired,
  Image: string.isRequired,
  FullName: string.isRequired,
  Position: arrayOf(string.isRequired).isRequired,
  Department: string.isRequired,
  PhoneNumber: string.isRequired,
  EmailAddress: string.isRequired,
  externalLinkText: string.isRequired
};

export default React.memo(StaffCard);
